"use client";

import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format, parseISO, isValid } from "date-fns";
// Bundled (inlined) so it can never 404 — fallback for a dead tenant logo.
import xaleLogo from "@/assets/Logo.png";
import {
  User,
  ArrowLeft,
  Building2,
  Lock,
  Bell,
  Shield,
  Pencil,
  Loader2,
  CheckCircle2,
  Mail,
  MapPin,
  CalendarClock,
  Sparkles,
  Crown,
  Globe,
  CreditCard,
  Phone as PhoneIcon,
  ShieldCheck,
  Copy,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/stores/hook";
import { getInitials } from "@/utils/convetors";
import axiosInstance from "@/lib/axios";
import { refreshSessionUser } from "@/lib/refreshSessionUser";
import { userEndpoint } from "@/config/endpoints";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

const NameSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
});

const PhoneSchema = z.object({
  phone: z.string().optional(),
});

const PasswordSchema = z
  .object({
    password: z.string().min(6, "At least 6 characters"),
    confirmPassword: z.string().min(6, "At least 6 characters"),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type SectionId = "general" | "organization" | "security" | "notifications";

const NAV_ITEMS: { id: SectionId; label: string; icon: typeof User }[] = [
  { id: "general", label: "Personal info", icon: User },
  { id: "organization", label: "Organization", icon: Building2 },
  { id: "security", label: "Security", icon: Lock },
  { id: "notifications", label: "Notifications", icon: Bell },
];

function formatSystemRoleLabel(role?: string): string {
  if (!role) return "";
  return role
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

function formatPlanStatus(status?: string): string {
  if (!status) return "—";
  return status
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

function humanizeKey(key?: string): string {
  if (!key) return "—";
  return key
    .replace(/^custom_field_/, "Custom field ")
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function permissionLabel(value: any): string {
  if (value === true || value === "allow") return "Full access";
  if (value === "manage") return "Manage";
  if (value === "edit") return "Edit";
  if (value === "view" || value === "own") return "View";
  return "";
}

export default function UserProfilePage() {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const user = currentUser?.user ?? currentUser;

  const [activeSection, setActiveSection] = useState<SectionId>("general");
  const [editingField, setEditingField] = useState<string | null>(null);

  const initials = getInitials(user?.name || "");

  const profileCompletion = useMemo(() => {
    const p = user?.profileCompletion;
    if (typeof p === "number" && !Number.isNaN(p)) {
      return Math.min(100, Math.max(0, Math.round(p)));
    }
    return 0;
  }, [user?.profileCompletion]);

  const branchAssignments = useMemo(() => {
    const raw = user?.branches;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((b: { isRevoked?: boolean }) => !b?.isRevoked)
      .map(
        (b: {
          branch?: { name?: string; code?: string | null };
        }) => ({
          name: b?.branch?.name ?? "Branch",
          code: b?.branch?.code,
        })
      );
  }, [user?.branches]);

  const tenant = user?.tenant;
  const roleName = user?.role?.name ?? user?.systemRole ?? "—";
  const systemRoleLabel = formatSystemRoleLabel(user?.systemRole);

  /** Preset role slug `superadmin` or name contains Super Admin — full RBAC picture in UI. */
  const isSuperAdminRole = useMemo(() => {
    const slug = String(user?.role?.slug ?? "").toLowerCase();
    const name = String(user?.role?.name ?? "").toLowerCase();
    if (slug === "superadmin") return true;
    if (name.includes("super admin")) return true;
    return false;
  }, [user?.role?.slug, user?.role?.name]);

  /** Tenant admins bypass permission engine; Super Admin is the top preset role. */
  const hasFullTenantAccess = Boolean(user?.isTenantAdmin || isSuperAdminRole);

  const accessConfig = useMemo(() => {
    return user?.role?.permissions?.[0]?.access_config ?? null;
  }, [user?.role?.permissions]);

  const enabledModules = useMemo(() => {
    const routeKeys = Array.isArray(accessConfig?.routeKeys)
      ? accessConfig.routeKeys
      : [];
    return routeKeys.slice(0, 8).map((key: string) => humanizeKey(key));
  }, [accessConfig?.routeKeys]);

  const permissionHighlights = useMemo(() => {
    if (hasFullTenantAccess) {
      return [
        { label: "All modules", value: "Full access" },
        { label: "Users & roles", value: "Manage" },
        { label: "Sensitive records", value: "Allowed" },
        { label: "Billing & setup", value: "Manage" },
      ];
    }

    const global = accessConfig?.global ?? {};
    const rows: Array<{ label: string; value: string }> = [];
    Object.entries(global).forEach(([key, raw]: [string, any]) => {
      if (rows.length >= 8) return;
      if (!raw || typeof raw !== "object") return;
      const level = raw.level ?? raw.view ?? raw.edit ?? raw.create ?? raw.manage;
      const label = permissionLabel(level);
      if (!label) return;
      rows.push({ label: humanizeKey(key), value: label });
    });
    return rows;
  }, [accessConfig?.global, hasFullTenantAccess]);

  const sourceScopeCount = Array.isArray(accessConfig?.sources)
    ? accessConfig.sources.length
    : 0;
  const stageScopeCount = Array.isArray(accessConfig?.stages)
    ? accessConfig.stages.length
    : 0;

  const memberSince = useMemo(() => {
    if (!user?.createdAt) return null;
    try {
      const d = parseISO(String(user.createdAt));
      return isValid(d) ? format(d, "d MMM yyyy") : null;
    } catch {
      return null;
    }
  }, [user?.createdAt]);

  const lastSyncedShort = useMemo(() => {
    if (!user?.lastLogsSyncedAt) return null;
    try {
      const d = parseISO(String(user.lastLogsSyncedAt));
      return isValid(d) ? format(d, "d MMM, h:mm a") : null;
    } catch {
      return null;
    }
  }, [user?.lastLogsSyncedAt]);

  const [copiedEmail, setCopiedEmail] = useState(false);
  const handleCopyEmail = async () => {
    if (!user?.email) return;
    try {
      await navigator.clipboard.writeText(String(user.email));
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 1600);
    } catch {
      /* silent */
    }
  };

  const trialEndsLabel = useMemo(() => {
    if (!tenant?.trialEndsAt) return null;
    try {
      const d = parseISO(String(tenant.trialEndsAt));
      return isValid(d) ? format(d, "d MMM yyyy") : null;
    } catch {
      return null;
    }
  }, [tenant?.trialEndsAt]);

  const refreshUser = async () => {
    try {
      await refreshSessionUser();
    } catch {
      /* silent */
    }
  };

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await axiosInstance.patch(
        `${userEndpoint}/${user?.id}`,
        data
      );
      return res.data;
    },
    onSuccess: () => {
      refreshUser();
      toast.success("Updated successfully");
      setEditingField(null);
    },
    onError: () => toast.error("Update failed"),
  });

  const passwordMutation = useMutation({
    mutationFn: async (data: { password: string; confirmPassword: string }) => {
      const res = await axiosInstance.post("/auth/set-password", data);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Password updated");
      passwordForm.reset();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to update password");
    },
  });

  const nameForm = useForm({
    resolver: zodResolver(NameSchema),
    defaultValues: { name: user?.name || "" },
  });

  const phoneForm = useForm({
    resolver: zodResolver(PhoneSchema),
    defaultValues: { phone: user?.phone || "" },
  });

  const passwordForm = useForm({
    resolver: zodResolver(PasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  return (
    <div className="h-(--screen-h) bg-[var(--color-background)] overflow-hidden flex flex-col">
      <header className="w-full bg-white h-header border-b border-[#e6e8e7] shrink-0">
        <div className="flex items-center justify-between h-full px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="size-8 rounded-xl bg-[#f7f9f8] border border-[#e6e8e7] flex items-center justify-center shrink-0 hover:bg-[#eef1f0] hover:border-[#d6ebe3] active:scale-95 transition-all duration-150"
            >
              <ArrowLeft className="size-4 text-[#505e59]" />
            </button>
            <p className="text-[15px] font-medium tracking-[-0.3px] text-[#1e302a]">
              Account Settings
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-hidden p-4 pb-6 px-6">
        <div className="settings-body h-full">
          {/* Sidebar */}
          <div className="settings-body-left flex flex-col">
            <div className="relative mb-4 overflow-hidden rounded-2xl border border-[#e6e8e7] bg-gradient-to-br from-[#f4faf7] via-white to-white p-3 shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
              <div className="pointer-events-none absolute -right-8 -top-8 size-24 rounded-full bg-[radial-gradient(circle,rgba(33,135,96,0.18),transparent_70%)] blur-xl" />
              <div className="relative flex items-center gap-3">
                <div className="relative shrink-0">
                  <div className="size-10 rounded-xl bg-white p-[2px] shadow-[0_2px_8px_-2px_rgba(5,25,18,0.18)]">
                    <div
                      className="flex size-full items-center justify-center rounded-[10px] text-[12px] font-semibold text-white"
                      style={{
                        background:
                          "linear-gradient(145deg, #1f7a55 0%, #0e3a28 100%)",
                      }}
                    >
                      {initials}
                    </div>
                  </div>
                  <span className="absolute -bottom-px -right-px size-3 rounded-full border-[2px] border-white bg-emerald-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold tracking-[-0.28px] text-[#051912]">
                    {user?.name}
                  </p>
                  <p className="flex items-center gap-1 truncate text-[10.5px] font-medium uppercase tracking-[0.04em] text-[#218760]">
                    {hasFullTenantAccess && <Crown className="size-2.5" />}
                    {user?.role?.name || user?.systemRole}
                  </p>
                </div>
              </div>
            </div>

            <nav className="flex flex-col gap-0.5 px-1">
              {NAV_ITEMS.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      setEditingField(null);
                    }}
                    className={`group/nav relative flex items-center gap-2.5 rounded-xl px-3 py-2 text-left transition-all duration-200 ${
                      isActive
                        ? "bg-white text-[#051912] shadow-[0_1px_2px_rgba(5,25,18,0.06),inset_0_0_0_1px_rgba(33,135,96,0.18)]"
                        : "text-[#697571] hover:bg-[#f7f9f8] hover:text-[#1e302a]"
                    }`}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full bg-[#218760]" />
                    )}
                    <span
                      className={`flex size-6 items-center justify-center rounded-md transition-colors ${
                        isActive
                          ? "bg-[#eaf5f1] text-[#218760]"
                          : "text-[#9ba3a0] group-hover/nav:text-[#505e59]"
                      }`}
                    >
                      <item.icon className="size-3.5" strokeWidth={2.25} />
                    </span>
                    <span className="text-[12.5px] font-medium tracking-[-0.24px]">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>

            <div className="mt-auto px-1 pt-4">
              <div className="rounded-xl border border-dashed border-[#d6ebe3] bg-[#f7fbf9] p-3">
                <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#218760]">
                  <Sparkles className="size-3" />
                  Workspace
                </p>
                <p className="mt-1 truncate text-[12px] font-semibold text-[#1e302a]">
                  {tenant?.tenantName ?? "—"}
                </p>
                <p className="text-[10.5px] text-[#828c88]">
                  {formatPlanStatus(tenant?.planStatus)} ·{" "}
                  {tenant?.currency ?? ""}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="settings-body-right !gap-3 w-full min-w-0">
            {activeSection === "general" && (
              <div className="animate-in fade-in duration-200 w-full min-w-0 space-y-5">
                {/* ───────────── HERO — magazine cover ───────────── */}
                <div className="relative overflow-hidden rounded-[28px] border border-[#e6e8e7] bg-white shadow-[0_1px_3px_rgba(5,25,18,0.04),0_24px_56px_-32px_rgba(21,101,72,0.22)]">
                  {/* Cover band */}
                  <div className="relative h-[148px] overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(135deg,#e8f3ed_0%,#d4ebde_28%,#f4faf7_62%,#fafdfb_100%)]" />
                    <div className="absolute -right-8 -top-12 size-56 rounded-full bg-[radial-gradient(circle,rgba(33,135,96,0.22),transparent_70%)] blur-2xl" />
                    <div className="absolute -bottom-16 left-[28%] size-52 rounded-full bg-[radial-gradient(circle,rgba(125,194,162,0.28),transparent_70%)] blur-2xl" />
                    <div className="absolute right-[18%] top-3 size-40 rounded-full bg-[radial-gradient(circle,rgba(167,217,194,0.35),transparent_70%)] blur-2xl" />
                    <div
                      className="absolute inset-0 opacity-[0.45]"
                      style={{
                        backgroundImage:
                          "linear-gradient(transparent 95%, rgba(33,135,96,0.07) 95%), linear-gradient(90deg, transparent 95%, rgba(33,135,96,0.07) 95%)",
                        backgroundSize: "22px 22px",
                      }}
                    />
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/70 to-transparent" />

                    {/* Top-right utility chip */}
                    <div className="absolute right-5 top-4 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/85 px-2.5 py-1 text-[10.5px] font-semibold text-[#156548] shadow-[0_1px_2px_rgba(5,25,18,0.06),inset_0_0_0_1px_rgba(33,135,96,0.18)] backdrop-blur">
                        <span className="relative flex size-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#218760] opacity-75" />
                          <span className="relative inline-flex size-1.5 rounded-full bg-[#218760]" />
                        </span>
                        Active now
                      </span>
                    </div>
                  </div>

                  {/* Identity row, overlapping the cover */}
                  <div className="relative -mt-14 px-5 pb-6 sm:px-7">
                    <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end">
                      {/* Squircle avatar */}
                      <div className="relative shrink-0">
                        <div className="size-[112px] rounded-[24px] bg-white p-[5px] shadow-[0_8px_28px_-6px_rgba(5,25,18,0.22),0_2px_8px_rgba(5,25,18,0.06)]">
                          <div
                            className="flex size-full items-center justify-center rounded-[20px] text-[34px] font-semibold tracking-[-0.7px] text-white"
                            style={{
                              background:
                                "linear-gradient(145deg, #1f7a55 0%, #0e3a28 100%)",
                            }}
                          >
                            {initials}
                          </div>
                        </div>
                        <span className="absolute bottom-1 right-1 size-4 rounded-full border-[3px] border-white bg-emerald-500 shadow-sm" />
                      </div>

                      {/* Name + identity */}
                      <div className="min-w-0 flex-1 pb-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.6px] text-[#051912] sm:text-[28px]">
                            {user?.name}
                          </h1>
                          <ShieldCheck className="size-[18px] shrink-0 text-[#218760]" />
                          {hasFullTenantAccess && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#218760] to-[#0e3a28] px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-[0.06em] text-white shadow-[0_2px_6px_rgba(33,135,96,0.35)]">
                              <Crown className="size-3" fill="currentColor" />
                              {isSuperAdminRole ? "Super Admin" : "Admin"}
                            </span>
                          )}
                          {!hasFullTenantAccess && roleName !== "—" && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#f0f2f1] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#505e59]">
                              <Sparkles className="size-3 text-[#218760]" />
                              {roleName}
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px] text-[#505e59]">
                          <button
                            type="button"
                            onClick={handleCopyEmail}
                            title={copiedEmail ? "Copied" : "Copy email"}
                            className="group/em inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 -ml-1.5 transition-colors hover:bg-[#f4faf7]"
                          >
                            <Mail className="size-3.5 text-[#9ba3a0]" />
                            <span className="truncate max-w-[280px] font-medium text-[#1e302a]">
                              {user?.email}
                            </span>
                            {copiedEmail ? (
                              <Check className="size-3 text-[#218760]" />
                            ) : (
                              <Copy className="size-3 text-[#cdd1d0] transition-colors group-hover/em:text-[#697571]" />
                            )}
                          </button>
                          {tenant?.tenantName && (
                            <>
                              <span className="text-[#cdd1d0]">·</span>
                              <span className="inline-flex items-center gap-1.5">
                                <Building2 className="size-3.5 text-[#9ba3a0]" />
                                <span className="font-medium text-[#1e302a]">
                                  {tenant.tenantName}
                                </span>
                              </span>
                            </>
                          )}
                          {memberSince && (
                            <>
                              <span className="text-[#cdd1d0]">·</span>
                              <span className="inline-flex items-center gap-1.5 text-[#697571]">
                                <CalendarClock className="size-3.5 text-[#9ba3a0]" />
                                Joined {memberSince}
                              </span>
                            </>
                          )}
                        </div>

                        <p className="mt-3 max-w-[680px] text-[12.5px] leading-relaxed text-[#697571]">
                          {hasFullTenantAccess ? (
                            isSuperAdminRole ? (
                              <>
                                <span className="font-semibold text-[#1e302a]">
                                  Super Admin
                                </span>{" "}
                                — full access to every module, setting, user, and
                                sensitive record. Permission checks do not apply
                                to your account.
                              </>
                            ) : (
                              <>
                                <span className="font-semibold text-[#1e302a]">
                                  Tenant admin
                                </span>{" "}
                                — permission rules are bypassed for your account.
                                You can access all tenant features your plan
                                includes.
                              </>
                            )
                          ) : (
                            user?.role?.description ||
                            "Your role defines what you can view and change across this workspace."
                          )}
                        </p>
                      </div>

                      {/* Action cluster */}
                      <div className="flex shrink-0 items-center gap-2 self-end pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSection("security");
                            setEditingField(null);
                          }}
                          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[#e6e8e7] bg-white px-3 text-[12.5px] font-medium text-[#374741] shadow-[0_1px_2px_rgba(5,25,18,0.04)] transition-all hover:border-[#d6ebe3] hover:bg-[#f7fbf9] hover:text-[#156548]"
                        >
                          <Lock className="size-3.5" />
                          Security
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingField("name");
                            nameForm.reset({ name: user?.name || "" });
                          }}
                          className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-[#218760] to-[#0e3a28] px-3.5 text-[12.5px] font-semibold text-white shadow-[0_2px_8px_-2px_rgba(33,135,96,0.5),inset_0_1px_0_rgba(255,255,255,0.18)] transition-all hover:shadow-[0_4px_12px_-2px_rgba(33,135,96,0.6),inset_0_1px_0_rgba(255,255,255,0.18)] active:scale-[0.98]"
                        >
                          <Pencil className="size-3.5" />
                          Edit profile
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ───────────── KPI STRIP ───────────── */}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Profile strength */}
                  <div className="group/kpi relative overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white p-4 shadow-[0_1px_2px_rgba(5,25,18,0.04)] transition-all hover:-translate-y-px hover:shadow-[0_8px_20px_-8px_rgba(33,135,96,0.18)]">
                    <div className="pointer-events-none absolute -right-4 -top-4 size-20 rounded-full bg-[radial-gradient(circle,rgba(33,135,96,0.12),transparent_70%)] blur-xl" />
                    <div className="relative flex items-center gap-3">
                      <div className="relative size-12 shrink-0">
                        <ProfileCompletionRing pct={profileCompletion} size={48} />
                        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums text-[#1e302a]">
                          {profileCompletion}%
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9ba3a0]">
                          Profile strength
                        </p>
                        <p className="mt-0.5 text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                          {profileCompletion >= 100
                            ? "Complete"
                            : profileCompletion >= 60
                              ? "Almost there"
                              : "Get started"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Plan */}
                  <KpiTile
                    icon={CreditCard}
                    iconBg="bg-[#eaf5f1]"
                    iconColor="text-[#218760]"
                    label="Plan"
                    value={formatPlanStatus(tenant?.planStatus)}
                    sub={tenant?.billingCycle ? formatPlanStatus(tenant.billingCycle) : undefined}
                  />

                  {/* Member since */}
                  <KpiTile
                    icon={CalendarClock}
                    iconBg="bg-[#eef2ff]"
                    iconColor="text-[#4f46e5]"
                    label="Member since"
                    value={memberSince ?? "—"}
                    sub={trialEndsLabel && String(tenant?.planStatus ?? "").toUpperCase() === "TRIAL" ? `Trial ends ${trialEndsLabel}` : undefined}
                  />

                  {/* Last activity */}
                  <KpiTile
                    icon={Globe}
                    iconBg="bg-[#fff7ed]"
                    iconColor="text-[#c2410c]"
                    label="Last activity"
                    value={lastSyncedShort ?? "—"}
                    sub={tenant?.timezone ?? undefined}
                  />
                </div>

                {/* ───────────── PLAN + BRANCHES ───────────── */}
                <div className="grid w-full gap-4 lg:grid-cols-5">
                  {/* Plan & Billing — wider */}
                  <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                    <div className="flex items-center justify-between border-b border-[#f0f2f1] px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex size-7 items-center justify-center rounded-lg bg-[#eaf5f1]">
                          <CreditCard className="size-3.5 text-[#218760]" />
                        </span>
                        <div>
                          <p className="text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                            Plan & billing
                          </p>
                          <p className="text-[10.5px] text-[#9ba3a0]">
                            Workspace subscription details
                          </p>
                        </div>
                      </div>
                      <span className="rounded-full bg-[#eaf5f1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#156548]">
                        {formatPlanStatus(tenant?.planStatus)}
                      </span>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-5 gap-y-3 px-5 py-4 text-[12px]">
                      {tenant?.billingCycle && (
                        <DetailRow label="Billing cycle" value={formatPlanStatus(tenant.billingCycle)} />
                      )}
                      {trialEndsLabel && String(tenant?.planStatus ?? "").toUpperCase() === "TRIAL" && (
                        <DetailRow label="Trial ends" value={trialEndsLabel} accent />
                      )}
                      <DetailRow label="Currency" value={tenant?.currency ?? "—"} />
                      {tenant?.timezone && (
                        <DetailRow label="Timezone" value={tenant.timezone} />
                      )}
                    </dl>
                  </div>

                  {/* Branches — wider */}
                  <div className="lg:col-span-3 relative overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                    <div className="flex items-center justify-between border-b border-[#f0f2f1] px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex size-7 items-center justify-center rounded-lg bg-[#fef3c7]">
                          <MapPin className="size-3.5 text-[#b45309]" />
                        </span>
                        <div>
                          <p className="text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                            Branch access
                          </p>
                          <p className="text-[10.5px] text-[#9ba3a0]">
                            Branches you can work across
                          </p>
                        </div>
                      </div>
                      <span className="text-[10.5px] font-medium text-[#9ba3a0]">
                        {branchAssignments.length > 0
                          ? `${branchAssignments.length} branch${branchAssignments.length === 1 ? "" : "es"}`
                          : hasFullTenantAccess
                            ? "Workspace-wide"
                            : "None"}
                      </span>
                    </div>
                    <div className="px-5 py-4">
                      {branchAssignments.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {branchAssignments.map((b, i) => (
                            <span
                              key={`${b.name}-${i}`}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e6e8e7] bg-[#fafcfb] py-1 pl-1 pr-2.5 text-[11.5px] font-medium text-[#1e302a]"
                            >
                              <span className="flex size-5 items-center justify-center rounded-md bg-[#eaf5f1] text-[10px] font-bold uppercase text-[#218760]">
                                {String(b.name ?? "?").slice(0, 1)}
                              </span>
                              {b.name}
                              {b.code ? (
                                <span className="text-[10px] font-normal text-[#9ba3a0]">
                                  {b.code}
                                </span>
                              ) : null}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-start gap-3 rounded-xl border border-dashed border-[#d6ebe3] bg-[#f7fbf9] px-3 py-2.5">
                          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-white text-[#218760] shadow-sm">
                            <Sparkles className="size-3.5" />
                          </span>
                          <p className="text-[11.5px] leading-relaxed text-[#505e59]">
                            {hasFullTenantAccess
                              ? isSuperAdminRole
                                ? "No branch tags — as Super Admin you can work across the workspace subject to plan and data visibility rules."
                                : "No branch tags — tenant admins bypass per-branch checks; branch filters may still apply where configured."
                              : "No branch assignments yet. Ask your admin for access to start working with leads."}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ───────────── BASIC INFORMATION ───────────── */}
                <div className="overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                  <div className="flex items-center justify-between border-b border-[#f0f2f1] px-5 py-3.5">
                    <div>
                      <p className="text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                        Basic information
                      </p>
                      <p className="text-[10.5px] text-[#9ba3a0]">
                        Update your account details. Email is managed by your admin.
                      </p>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-[#f0f2f1] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#697571]">
                      <Pencil className="size-2.5" />
                      Inline edit
                    </span>
                  </div>

                  <ProfileField
                    label="Full name"
                    icon={User}
                    value={user?.name}
                    isEditing={editingField === "name"}
                    onEdit={() => {
                      setEditingField("name");
                      nameForm.reset({ name: user?.name || "" });
                    }}
                    onCancel={() => setEditingField(null)}
                  >
                    <form
                      onSubmit={nameForm.handleSubmit((v) =>
                        updateMutation.mutate({ name: v.name })
                      )}
                      className="flex flex-1 items-center gap-2"
                    >
                      <Input
                        {...nameForm.register("name")}
                        autoFocus
                        className="h-9 max-w-[260px] rounded-lg border-[#d6ebe3] bg-[#fafcfb] text-[13px] focus:border-[#218760] focus:bg-white focus:ring-2 focus:ring-[#d6ebe3]"
                      />
                      <button
                        type="submit"
                        disabled={updateMutation.isPending}
                        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-gradient-to-br from-[#218760] to-[#0e3a28] px-3.5 text-[12px] font-semibold text-white shadow-[0_1px_2px_rgba(5,25,18,0.06),inset_0_1px_0_rgba(255,255,255,0.18)] transition-all hover:shadow-[0_4px_12px_-2px_rgba(33,135,96,0.45)] active:scale-[0.98]"
                      >
                        {updateMutation.isPending ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingField(null)}
                        className="inline-flex h-9 shrink-0 items-center rounded-lg border border-[#e6e8e7] px-3 text-[12px] font-medium text-[#697571] transition-colors hover:border-[#d6ebe3] hover:bg-[#f7fbf9] hover:text-[#1e302a]"
                      >
                        Cancel
                      </button>
                    </form>
                    {nameForm.formState.errors.name && (
                      <p className="mt-1 text-[10.5px] font-medium text-red-500">
                        {nameForm.formState.errors.name.message as string}
                      </p>
                    )}
                  </ProfileField>

                  <ProfileField
                    label="Email"
                    icon={Mail}
                    value={user?.email}
                    readonly
                    hint="Locked — contact your admin to change email"
                    lockIcon
                  />

                  <ProfileField
                    label="Phone"
                    icon={PhoneIcon}
                    value={user?.phone || "Add a phone number"}
                    isEmpty={!user?.phone}
                    isEditing={editingField === "phone"}
                    onEdit={() => {
                      setEditingField("phone");
                      phoneForm.reset({ phone: user?.phone || "" });
                    }}
                    onCancel={() => setEditingField(null)}
                  >
                    <form
                      onSubmit={phoneForm.handleSubmit((v) =>
                        updateMutation.mutate({ phone: v.phone })
                      )}
                      className="flex flex-1 items-center gap-2"
                    >
                      <Input
                        {...phoneForm.register("phone")}
                        autoFocus
                        placeholder="+1 (555) 000-0000"
                        className="h-9 max-w-[240px] rounded-lg border-[#d6ebe3] bg-[#fafcfb] text-[13px] focus:border-[#218760] focus:bg-white focus:ring-2 focus:ring-[#d6ebe3]"
                      />
                      <button
                        type="submit"
                        disabled={updateMutation.isPending}
                        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-gradient-to-br from-[#218760] to-[#0e3a28] px-3.5 text-[12px] font-semibold text-white shadow-[0_1px_2px_rgba(5,25,18,0.06),inset_0_1px_0_rgba(255,255,255,0.18)] transition-all hover:shadow-[0_4px_12px_-2px_rgba(33,135,96,0.45)] active:scale-[0.98]"
                      >
                        {updateMutation.isPending ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingField(null)}
                        className="inline-flex h-9 shrink-0 items-center rounded-lg border border-[#e6e8e7] px-3 text-[12px] font-medium text-[#697571] transition-colors hover:border-[#d6ebe3] hover:bg-[#f7fbf9] hover:text-[#1e302a]"
                      >
                        Cancel
                      </button>
                    </form>
                  </ProfileField>

                  <ProfileField
                    label="Role"
                    icon={Shield}
                    value={user?.role?.name || systemRoleLabel || "—"}
                    badgeRight={
                      hasFullTenantAccess ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#218760] to-[#0e3a28] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.06em] text-white shadow-sm">
                          <Crown className="size-2.5" fill="currentColor" />
                          {isSuperAdminRole ? "Super" : "Admin"}
                        </span>
                      ) : null
                    }
                    readonly
                    isLast
                    onJump={() => setActiveSection("organization")}
                  />
                </div>
              </div>
            )}

            {activeSection === "organization" && (
              <div className="animate-in fade-in duration-200 space-y-4">
                <div className="overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                  <div className="flex items-start justify-between gap-4 border-b border-[#f0f2f1] px-5 py-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[#eaf5f1] text-[#218760]">
                        {user?.tenant?.tenantLogoUrl ? (
                          <img
                            src={user.tenant.tenantLogoUrl}
                            alt=""
                            className="size-8 rounded-xl object-contain"
                            onError={(e) => {
                              // Dead / deleted tenant logo URL — fall back to
                              // the bundled Xale logo (guarded to fire once).
                              const img = e.currentTarget;
                              if (img.dataset.fallback) return;
                              img.dataset.fallback = "1";
                              img.src = xaleLogo;
                            }}
                          />
                        ) : (
                          <Building2 className="size-5" />
                        )}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-semibold tracking-[-0.3px] text-[#051912]">
                          {tenant?.tenantName || "Workspace"}
                        </p>
                        <p className="mt-0.5 text-[11.5px] leading-relaxed text-[#697571]">
                          International CRM workspace ·{" "}
                          {tenant?.timezone || "Timezone not set"}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#eaf5f1] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[#156548]">
                      <CheckCircle2 className="size-3" />
                      Active
                    </span>
                  </div>

                  <div className="grid gap-0 border-b border-[#f0f2f1] md:grid-cols-3">
                    <CompactInfo label="Plan" value={formatPlanStatus(tenant?.planStatus)} />
                    <CompactInfo label="Currency" value={tenant?.currency ?? "—"} />
                    <CompactInfo label="Billing" value={tenant?.billingCycle ? formatPlanStatus(tenant.billingCycle) : "—"} />
                  </div>

                  <div className="px-5 py-4">
                    <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9ba3a0]">
                      Workspace scope
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      <AccessStat
                        icon={MapPin}
                        label="Branches"
                        value={
                          branchAssignments.length > 0
                            ? `${branchAssignments.length}`
                            : hasFullTenantAccess
                              ? "All"
                              : "0"
                        }
                        hint={hasFullTenantAccess ? "Workspace-wide" : "Assigned access"}
                      />
                      <AccessStat
                        icon={Shield}
                        label="Stages"
                        value={hasFullTenantAccess ? "All" : String(stageScopeCount)}
                        hint="Pipeline visibility"
                      />
                      <AccessStat
                        icon={Globe}
                        label="Sources"
                        value={hasFullTenantAccess ? "All" : String(sourceScopeCount)}
                        hint="Lead origin access"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-5">
                  <div className="lg:col-span-2 overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                    <div className="border-b border-[#f0f2f1] px-5 py-4">
                      <p className="text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                        Role summary
                      </p>
                      <p className="mt-0.5 text-[10.5px] text-[#9ba3a0]">
                        What your account is trusted to do
                      </p>
                    </div>
                    <div className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="flex size-9 items-center justify-center rounded-xl bg-[#eaf5f1] text-[#218760]">
                          {hasFullTenantAccess ? (
                            <Crown className="size-4" />
                          ) : (
                            <ShieldCheck className="size-4" />
                          )}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-[13.5px] font-semibold text-[#051912]">
                            {user?.role?.name || systemRoleLabel || "Role"}
                          </p>
                          <p className="text-[10.5px] text-[#828c88]">
                            {hasFullTenantAccess ? "Admin bypass enabled" : "Permission based access"}
                          </p>
                        </div>
                      </div>
                      <p className="mt-3 text-[11.5px] leading-relaxed text-[#697571]">
                        {hasFullTenantAccess
                          ? "This account can work across every module included in the workspace plan."
                          : user?.role?.description ||
                            "Your role controls the modules, stages, sources, and records visible to you."}
                      </p>
                    </div>
                  </div>

                  <div className="lg:col-span-3 overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                    <div className="flex items-center justify-between border-b border-[#f0f2f1] px-5 py-4">
                      <div>
                        <p className="text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                          Allowed permissions
                        </p>
                        <p className="mt-0.5 text-[10.5px] text-[#9ba3a0]">
                          Concise view of your role rules
                        </p>
                      </div>
                      <span className="rounded-full bg-[#f7f9f8] px-2 py-0.5 text-[10px] font-semibold text-[#697571]">
                        {hasFullTenantAccess ? "Full" : `${permissionHighlights.length} shown`}
                      </span>
                    </div>
                    <div className="px-5 py-4">
                      {permissionHighlights.length > 0 ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          {permissionHighlights.map((item) => (
                            <PermissionLine
                              key={`${item.label}-${item.value}`}
                              label={item.label}
                              value={item.value}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="rounded-xl border border-dashed border-[#e6e8e7] bg-[#fafcfb] px-3 py-2.5 text-[11.5px] text-[#697571]">
                          No explicit permission summary is available for this role.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white shadow-[0_1px_2px_rgba(5,25,18,0.04)]">
                  <div className="flex items-center justify-between border-b border-[#f0f2f1] px-5 py-4">
                    <div>
                      <p className="text-[13px] font-semibold tracking-[-0.26px] text-[#1e302a]">
                        Modules & regions
                      </p>
                      <p className="mt-0.5 text-[10.5px] text-[#9ba3a0]">
                        Useful for international teams working across branches, countries, and sources
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-4 px-5 py-4 lg:grid-cols-2">
                    <TagPanel
                      title="Enabled modules"
                      emptyText={hasFullTenantAccess ? "All modules are available." : "No modules assigned."}
                      tags={hasFullTenantAccess ? ["All CRM modules"] : enabledModules}
                    />
                    <TagPanel
                      title="Branch access"
                      emptyText={
                        hasFullTenantAccess
                          ? "Workspace-wide access. No branch tag needed."
                          : "No branch assignments yet."
                      }
                      tags={branchAssignments.map((b) => b.code ? `${b.name} · ${b.code}` : b.name)}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeSection === "security" && (
              <div className="animate-in fade-in duration-200 space-y-3">
                <div className="bg-white rounded-2xl p-4">
                  <p className="text-[10px] font-semibold tracking-[0.5px] uppercase text-[#9ba3a0] mb-3">
                    Change Password
                  </p>
                  <form
                    onSubmit={passwordForm.handleSubmit((v) =>
                      passwordMutation.mutate(v)
                    )}
                    className="space-y-3 max-w-sm"
                  >
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-[#697571] block">
                        New Password
                      </label>
                      <Input
                        type="password"
                        {...passwordForm.register("password")}
                        className="h-8 text-[13px] rounded-lg border-[#e6e8e7] bg-[#f7f9f8] focus:bg-white focus:border-[#218760] focus:ring-1 focus:ring-[#d6ebe3]"
                      />
                      {passwordForm.formState.errors.password && (
                        <p className="text-[10px] text-red-500">
                          {passwordForm.formState.errors.password.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-[#697571] block">
                        Confirm Password
                      </label>
                      <Input
                        type="password"
                        {...passwordForm.register("confirmPassword")}
                        className="h-8 text-[13px] rounded-lg border-[#e6e8e7] bg-[#f7f9f8] focus:bg-white focus:border-[#218760] focus:ring-1 focus:ring-[#d6ebe3]"
                      />
                      {passwordForm.formState.errors.confirmPassword && (
                        <p className="text-[10px] text-red-500">
                          {
                            passwordForm.formState.errors.confirmPassword
                              .message
                          }
                        </p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={passwordMutation.isPending}
                      className="primary-btn px-4 h-8 text-[12px] font-medium flex items-center gap-1.5"
                    >
                      {passwordMutation.isPending && (
                        <Loader2 className="size-3 animate-spin" />
                      )}
                      Update Password
                    </button>
                  </form>
                </div>

                <div className="bg-white rounded-2xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-medium text-[#697571]">
                      Two-Factor Authentication
                    </p>
                    <p className="text-[11px] text-[#b4bab8]">
                      Add an extra layer of security.
                    </p>
                  </div>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#f0f2f1] text-[#9ba3a0]">
                    Coming Soon
                  </span>
                </div>
              </div>
            )}

            {activeSection === "notifications" && (
              <div className="animate-in fade-in duration-200 flex flex-col items-center justify-center py-16 text-center">
                <div className="size-12 rounded-2xl bg-[#eaf5f1] flex items-center justify-center mb-3">
                  <Bell className="size-5 text-[#218760]" />
                </div>
                <p className="text-[14px] font-medium text-[#1e302a] mb-1">
                  Managed by your organization
                </p>
                <p className="text-[11px] text-[#828c88] max-w-[240px] leading-relaxed">
                  Notification preferences are enforced by your admin policy.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileCompletionRing({
  pct,
  size = 116,
}: {
  pct: number;
  size?: number;
}) {
  const stroke = size <= 56 ? 4 : 5;
  const r = size / 2 - stroke;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <svg
      className="absolute inset-0 -rotate-90"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#eef1f0"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="url(#ringGrad)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        className="transition-all duration-700 ease-out"
      />
      <defs>
        <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#46a580" />
          <stop offset="100%" stopColor="#0e3a28" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function KpiTile({
  icon: Icon,
  iconBg,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: typeof User;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="group/kpi relative overflow-hidden rounded-2xl border border-[#e6e8e7] bg-white p-4 shadow-[0_1px_2px_rgba(5,25,18,0.04)] transition-all hover:-translate-y-px hover:shadow-[0_8px_20px_-8px_rgba(5,25,18,0.12)]">
      <div className="flex items-start gap-3">
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${iconBg}`}
        >
          <Icon className={`size-4 ${iconColor}`} strokeWidth={2.25} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9ba3a0]">
            {label}
          </p>
          <p className="mt-0.5 truncate text-[13.5px] font-semibold tracking-[-0.26px] text-[#1e302a]">
            {value}
          </p>
          {sub ? (
            <p className="mt-0.5 truncate text-[10.5px] text-[#828c88]">
              {sub}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9ba3a0]">
        {label}
      </dt>
      <dd
        className={`text-[12.5px] font-semibold tracking-[-0.24px] ${
          accent ? "text-[#b45309]" : "text-[#1e302a]"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function CompactInfo({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-[#f0f2f1] px-5 py-3 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9ba3a0]">
        {label}
      </p>
      <p className="mt-1 truncate text-[12.5px] font-semibold text-[#1e302a]">
        {value}
      </p>
    </div>
  );
}

function AccessStat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof User;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-[#e6e8e7] bg-[#fafcfb] p-3">
      <div className="flex items-center gap-2">
        <span className="flex size-8 items-center justify-center rounded-lg bg-white text-[#218760] shadow-sm">
          <Icon className="size-3.5" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9ba3a0]">
            {label}
          </p>
          <p className="text-[16px] font-semibold leading-tight text-[#051912]">
            {value}
          </p>
        </div>
      </div>
      <p className="mt-2 text-[10.5px] text-[#828c88]">{hint}</p>
    </div>
  );
}

function PermissionLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#e6e8e7] bg-[#fafcfb] px-3 py-2.5">
      <span className="min-w-0 truncate text-[12px] font-medium text-[#1e302a]">
        {label}
      </span>
      <span className="shrink-0 rounded-full bg-[#eaf5f1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#156548]">
        {value}
      </span>
    </div>
  );
}

function TagPanel({
  title,
  tags,
  emptyText,
}: {
  title: string;
  tags: string[];
  emptyText: string;
}) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9ba3a0]">
        {title}
      </p>
      {tags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-lg border border-[#e6e8e7] bg-[#fafcfb] px-2.5 py-1 text-[11.5px] font-medium text-[#1e302a]"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 rounded-xl border border-dashed border-[#e6e8e7] bg-[#fafcfb] px-3 py-2 text-[11.5px] text-[#697571]">
          {emptyText}
        </p>
      )}
    </div>
  );
}

function ProfileField({
  label,
  value,
  readonly,
  hint,
  isEditing,
  onEdit,
  onCancel: _onCancel,
  isEmpty,
  isLast,
  icon: Icon,
  lockIcon,
  badgeRight,
  onJump,
  children,
}: {
  label: string;
  value?: string;
  readonly?: boolean;
  hint?: string;
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  isEmpty?: boolean;
  isLast?: boolean;
  icon?: typeof User;
  lockIcon?: boolean;
  badgeRight?: React.ReactNode;
  onJump?: () => void;
  children?: React.ReactNode;
}) {
  if (isEditing && children) {
    return (
      <div
        className={`bg-[#fafcfb] px-5 py-4 ${
          !isLast ? "border-b border-[#f0f2f1]" : ""
        }`}
      >
        <div className="mb-2 flex items-center gap-2">
          {Icon ? (
            <span className="flex size-6 items-center justify-center rounded-md bg-white text-[#218760] shadow-sm ring-1 ring-[#e6e8e7]">
              <Icon className="size-3.5" strokeWidth={2.25} />
            </span>
          ) : null}
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#697571]">
            {label}
          </p>
        </div>
        {children}
      </div>
    );
  }

  const interactive = !readonly;

  return (
    <div
      className={`group/field flex items-center gap-4 px-5 py-3.5 transition-colors ${
        !isLast ? "border-b border-[#f0f2f1]" : ""
      } ${interactive ? "cursor-pointer hover:bg-[#fafcfb]" : ""}`}
      onClick={interactive ? onEdit : onJump}
    >
      {Icon ? (
        <span
          className={`flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
            readonly
              ? "bg-[#f7f9f8] text-[#9ba3a0]"
              : "bg-[#eaf5f1] text-[#218760] group-hover/field:bg-[#dff0e7]"
          }`}
        >
          <Icon className="size-4" strokeWidth={2.25} />
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#9ba3a0]">
          {label}
        </p>
        <div className="mt-0.5 flex items-center gap-2">
          <p
            className={`truncate text-[13.5px] tracking-[-0.26px] ${
              isEmpty
                ? "italic text-[#b4bab8]"
                : "font-medium text-[#1e302a]"
            }`}
          >
            {value}
          </p>
          {badgeRight}
        </div>
        {hint ? (
          <p className="mt-0.5 text-[10.5px] text-[#9ba3a0]">{hint}</p>
        ) : null}
      </div>

      {readonly ? (
        lockIcon ? (
          <Lock className="size-3.5 shrink-0 text-[#cdd1d0]" />
        ) : onJump ? (
          <ArrowUpRight className="size-3.5 shrink-0 text-[#cdd1d0] transition-colors group-hover/field:text-[#218760]" />
        ) : null
      ) : (
        <span className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-transparent px-2 text-[11px] font-medium text-[#9ba3a0] transition-all group-hover/field:border-[#d6ebe3] group-hover/field:bg-white group-hover/field:text-[#156548] group-hover/field:shadow-sm">
          <Pencil className="size-3" />
          Edit
        </span>
      )}
    </div>
  );
}
